import type { SvgWrapperProps } from '@ipfabric/ui-components/SvgWrapper/SvgWrapper';
import type { Layout } from '@ipfabric/utils';
import { CircularIcon } from 'components/Graph/GraphLayout/icons/CircularIcon';
import { DownwardTreeIcon } from 'components/Graph/GraphLayout/icons/DownwardTreeIcon';
import { HierarchicalIcon } from 'components/Graph/GraphLayout/icons/HierarchicalIcon';
import { RadialIcon } from 'components/Graph/GraphLayout/icons/RadialIcon';
import { UniversalIcon } from 'components/Graph/GraphLayout/icons/UniversalIcon';
import { UpwardTreeIcon } from 'components/Graph/GraphLayout/icons/UpwardTreeIcon';
import { UserDefinedIcon } from 'components/Graph/GraphLayout/icons/UserDefinedIcon';
import { pipe } from 'fp-ts/lib/function';
import { Texture } from 'pixi.js';
import type { ComponentType } from 'react';
import ReactDOMServer from 'react-dom/server';
import { waitForTextureLoad } from 'utils/pixi/waitForTextureLoad';

interface ButtonStyle {
  bgColorBottom: string;
  bgColorTop: string;
  contentColor: string;
  stroke?: string;
}

const drawButton =
  (attrs: ButtonStyle) =>
  (content: string): string =>
    `<svg width="240" height="240" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  ${
    attrs.stroke
      ? `<rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="url(#paint0_linear)" stroke="${attrs.stroke}" />`
      : '<rect width="24" height="24" rx="4" fill="url(#paint0_linear)" />'
  }
  ${content}
  <defs>
    <linearGradient id="paint0_linear" x1="12" y1="24" x2="12" y2="0" gradientUnits="userSpaceOnUse">
      <stop stop-color="${attrs.bgColorBottom}"/>
      <stop offset="1" stop-color="${attrs.bgColorTop}"/>
    </linearGradient>
  </defs>
</svg>
`;

const buttonGreen: ButtonStyle = {
  bgColorBottom: '#46AE7F',
  bgColorTop: '#56C291',
  contentColor: '#FFF',
};

const buttonGrey: ButtonStyle = {
  bgColorBottom: '#F5F5F5',
  bgColorTop: '#FFF',
  contentColor: '#333',
  stroke: '#D6D6D6',
};

export type PathLayoutButtons = { [Key in Layout | 'unsaved']: Texture };

const createButtonTexture = (
  Icon: ComponentType<SvgWrapperProps>,
  style: ButtonStyle,
  title: string,
): Texture =>
  pipe(
    <Icon
      width='16'
      height='16'
      x='4'
      y='4'
      color={style.contentColor}
      title={title}
    />,
    ReactDOMServer.renderToString,
    drawButton(style),
    Texture.from,
  );

export const loadPathLayoutButtons = async (): Promise<PathLayoutButtons> => {
  const buttonTextures: PathLayoutButtons = {
    circular: createButtonTexture(CircularIcon, buttonGrey, 'Circular layout'),
    downwardTree: createButtonTexture(
      DownwardTreeIcon,
      buttonGrey,
      'Downward tree layout',
    ),
    hierarchical: createButtonTexture(
      HierarchicalIcon,
      buttonGrey,
      'Hierarchical layout',
    ),
    radial: createButtonTexture(RadialIcon, buttonGrey, 'Radial layout'),
    universal: createButtonTexture(
      UniversalIcon,
      buttonGrey,
      'Universal layout',
    ),
    unsaved: createButtonTexture(
      UserDefinedIcon,
      buttonGreen,
      'Unsaved layout',
    ),
    upwardTree: createButtonTexture(
      UpwardTreeIcon,
      buttonGrey,
      'Upward tree layout',
    ),
    userDefined: createButtonTexture(
      UserDefinedIcon,
      buttonGrey,
      'User defined layout',
    ),
  };
  await Promise.all(Object.values(buttonTextures).map(waitForTextureLoad));
  return buttonTextures;
};
